import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import DocumentGallery from '@product-framework/components/DocumentGallery';
import Layout from '@product-framework/components/Layout';
import { IconLibrary, PageSection } from '@product-framework/index';
import {
  Breadcrumb,
  SnackbarApiErrorMessage,
  useDataProvider,
  useLocalization,
  LoadingEvent,
  useStore,
} from '@ui-framework/index';
import { useAppVersion } from '@product-framework/router/AppVersionContext';
import PublicHelpLayout from '../public/layout';
import getHelpDocuments from '../../api';
import HelpDocumentCard from '../../HelpDocumentCard';

function Help({ publicMode = false }) {
  const { formatMessage } = useLocalization();
  const navigate = useNavigate();
  const store = useStore();
  const language = store.domain.locale.get().toLowerCase();
  const provider = useDataProvider('issuesTracking');
  const { appVersion, loading: versionLoading } = useAppVersion();
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const storeName = publicMode ? 'HelpPagesPublic' : 'HelpPagesInternal';
  const { paginationState } = store.domain.list.get(storeName, {
    paginationState: { pageIndex: 1, pageSize: 6 },
  });
  const page = paginationState.pageIndex || 1;
  const limit = paginationState.pageSize;
  const breadcrumbs = [
    {
      name: formatMessage({
        id: 'HELP_TRAINING',
        defaultMessage: 'Help & Training',
      }),
      icon: IconLibrary.Help,
    },
  ];
  const PageLayout = publicMode ? PublicHelpLayout : Layout;

  useEffect(() => {
    if (versionLoading || !appVersion) return undefined;
    const controller = new AbortController();
    setLoading(true);
    getHelpDocuments(
      provider,
      { appVersion, language, page, limit },
      { publicMode, signal: controller.signal },
    ).then(response => {
      if (!controller.signal.aborted) {
        setResult(response);
        setLoading(false);
      }
    });
    return () => controller.abort();
  }, [provider, appVersion, versionLoading, language, page, limit, publicMode]);

  if (!versionLoading && !appVersion) {
    return (
      <PageLayout {...(publicMode ? { breadcrumbs } : {})}>
        <Alert severity="error">
          {formatMessage({
            id: 'HELP_VERSION_UNAVAILABLE',
            defaultMessage:
              'Unable to determine the app version. Reload the page to try again.',
          })}
        </Alert>
      </PageLayout>
    );
  }

  return (
    <PageLayout {...(publicMode ? { breadcrumbs } : {})}>
      {!publicMode && <Breadcrumb items={breadcrumbs} />}
      <PageSection label={breadcrumbs[0].name}>
        {(loading || versionLoading) && (
          <LoadingEvent>
            {formatMessage({
              id: 'FETCHING_HELP_DOCUMENTS',
              defaultMessage: 'Fetching help documents...',
            })}
          </LoadingEvent>
        )}
        {!loading && !versionLoading && !result?.ok && (
          <Alert severity="error">
            <SnackbarApiErrorMessage errors={result?.errors} />
          </Alert>
        )}
        {!loading && !versionLoading && result?.ok && (
          <DocumentGallery
            CardComponent={HelpDocumentCard}
            showPagination
            itemsPerRow={publicMode ? 2 : 3}
            data={(result.data?.data || []).map(document => ({
              ...document,
              slugUrl: document.slug,
              usedFallbackLanguage:
                document.languageCode !== language &&
                document.languageCode !== language.split('-')[0],
            }))}
            totalItems={result.data?.total || 0}
            paginationState={{ ...paginationState, pageIndex: page }}
            onCardClick={document =>
              navigate(
                `${publicMode ? '/public/help' : '/help/view'}/${encodeURIComponent(document.slug)}`,
              )
            }
            onPaginationChange={next =>
              store.domain.list.set(storeName, { paginationState: next })
            }
          />
        )}
      </PageSection>
    </PageLayout>
  );
}
export default observer(Help);
